import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import {useSelector, useDispatch} from "react-redux";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FlagCircleIcon from '@mui/icons-material/FlagCircle';
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import Typography from "@mui/material/Typography";
import AccordionDetails from "@mui/material/AccordionDetails";
import Accordion from "@mui/material/Accordion";
import { reorderQuestionnaireCategory, removeQuestionnaireCategory } from "../../../actions/questionnaireEditorActions";
import { getCategoryTemplate } from "../../../actions/categoryTemplateActions";
import { expanderCollapseSelected, expanderPreset, expanderCollapseAll } from "../../../actions/expanderActions";
import GroupViewerExpandableHook from "../groups/GroupViewerExpandableHook";
import ArgoReorderArrowsHook from "../../common/ArgoReorderArrowsHook";
import ArgoIconButtonHook from "../../common/ArgoIconButtonHook";
import ArgoTemplateListDividerHook from "../../common/ArgoTemplateListDividerHook";
import ArgoExpandCollapseButtonHook from "../../common/ArgoExpandCollapseButtonHook";
import statusFormatter from "../../../utilities/StatusFormatter";
import * as expander from "../../../utilities/expander";
import * as TEMPLATE from "../../../constants/templateConstants";
import * as portalShared from "../../../portal/shared/metadataConstantsAndUtilities";
import Box from "@mui/material/Box";


function CategoryViewerExpandableHook(props) {

    // const [portalPreviewRendered, setPortalPreviewRendered] = useState(false);
    // const [groupInfoDrawerOpen, setGroupInfoDrawerOpen] = useState(false);
    // const [expanderObjLoaded, setExpanderObjLoaded] = useState(false);
    const [widthChange, setWidthChange] = useState(false);

    //const [allRequiredTemplatesLoadedFromGraphFlag, setAllRequiredTemplatesLoadedFromGraphFlag] = useState(false);
    // const [allRequiredTemplatesLoadedFlag, setAllRequiredTemplatesLoadedFlag] = useState(false);
    //const [allTemplateGetsCalled, setAllTemplateGetsCalledFlag] = useState(false);
    const [categoryKeysArray, setCategoryKeysArray] = useState([]);
    const [categoryTemplateObjects, setCategoryTemplateObjects] = useState({});

    const dispatch = useDispatch();

    const categoryTemplatesFromSelector = useSelector(state => state.categoryTemplate);

    // Get expander state from Redux
    const reduxExpander = useSelector(state => {
        if (state.expander && state.expander[props.parentName])
        {
            return state.expander[props.parentName];
        } else {
            return {expanderSet: false, expandAll: true, expander: {}};
        }
    });

    // Similar to componentDidMount
    useEffect(() => {

        setExpander(props);
        
        // Add resize event listener
        window.addEventListener('resize', () => setWidthChange(!widthChange));
        
        // Clean up the event listener when component unmounts
        return () => {
            window.removeEventListener('resize', () => setWidthChange(!widthChange));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {

            if (props.useGraph) {

                // Graphs are a constant, once loaded they will not be changed because they are NOT used on screens that have edit capabilities

                if (props.questionnaireGraph["id"]) {
                    let questionnaireGraphClone = portalShared.cloneObj(props.questionnaireGraph);
                    let categories = questionnaireGraphClone.questionCategoryTemplates;

                    let categoryKeys = [];
                    let categoryObjs = {};
                    for (let c = 0; c < categories.length; c++) {

                        categoryKeys.push(categories[c].templateKey);

                        let groupKeys = [];
                        for (let g = 0; g < categories[c].groupTemplates.length; g++) {
                            groupKeys.push(categories[c].groupTemplates[g].templateKey);
                        }
                        categories[c].groupKeys = groupKeys; // Add groupKeys to the object
                        categoryObjs[categories[c].templateKey] = categories[c];

                    }

                    setCategoryKeysArray(categoryKeys);
                    setCategoryTemplateObjects(categoryObjs);
                    //setAllRequiredTemplatesLoadedFromGraphFlag(true);

                }

            } else {

                // Need to load all templates, use templateKey only so current template gets loaded
                if (props.categoryKeys?.length >= 0) {

                    props.categoryKeys.forEach((categoryKey) => {
                        dispatch(getCategoryTemplate(categoryKey, null));
                    });

                    setCategoryKeysArray(props.categoryKeys);
                    setCategoryTemplateObjects(categoryTemplatesFromSelector);

                }

            }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(props.categoryKeys), categoryTemplatesFromSelector, props.questionnaireGraph]);


    useEffect(() => {

        if (categoryKeysArray.length && allRequiredTemplatesLoaded()) {
            setExpander(props);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(categoryKeysArray), categoryTemplateObjects]);

    const setExpander = (props) => {
        if (allRequiredTemplatesLoaded()) {
            dispatch(expanderPreset(props.parentName, TEMPLATE.TYPE.CATEGORY, categoryKeysArray, categoryTemplateObjects, props.parentKey))

            props.setStateCallback({
                categoriesLoaded: true,
                categoryTemplates: categoryTemplateObjects
            });
        }

        // Not sure why but this component will re-mount after review screen back button
        // So putting in a fix to set expander to look at props.expander
        // if (allRequiredTemplatesLoaded()) {
        //     setExpanderPreset(true);
        //     setExpanderState(expander.presetExpander(props.categoryKeys, props.categoryTemplates, props));
        //     props.setStateCallback({ categoriesLoaded: true, categoryTemplates: props.categoryTemplates });
        // }
    };


    const allRequiredTemplatesLoaded = () => {
        let allTemplatesLoaded = (categoryKeysArray.length && (categoryKeysArray.every(key => Object.keys(categoryTemplateObjects).includes(key))));
        return allTemplatesLoaded;
    };

    // -----------------------------------------------------------------------------------------------------------------
    // EXPANDER FUNCTIONS ----------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------

    const onClickIconButtonReorder = (event, fieldName, index, depth, direction) => {
        event.stopPropagation(); // Keep accordion from expand/collapse
        dispatch(reorderQuestionnaireCategory((direction === "up"), index));
    };

    const onClickIconButtonEdit = (categoryTemplate, event) => {
        event.stopPropagation(); // Keep accordion from expand/collapse
        props.setStateCallback({ openCategoryEditor: true, categoryTemplate: categoryTemplate });
    };

    const onClickIconButtonRemove = (index, event) => {
        event.stopPropagation(); // Keep accordion from expand/collapse
        dispatch(removeQuestionnaireCategory(index));
    };

    const onClickIconButtonInfo = (categoryTemplate, event) => {

        let selectedTemplate = categoryTemplate; // Use template unless graph mode
        if (props.useGraph) {
            selectedTemplate = props.questionnaireGraph.questionCategoryTemplates.find(obj => {return obj.id === categoryTemplate.id});
        }

        event.stopPropagation(); // Keep accordion from expand/collapse
        props.setStateCallback({ openCategoryReview: true,
            selectedCategoryInfoKey: categoryTemplate.templateKey,
            selectedCategoryInfoId: categoryTemplate.id,
            selectedCategoryInfoTemplate: selectedTemplate});
    };

    const onExpandChange = (categoryTemplateKey) => {
        dispatch(expanderCollapseSelected(props.parentName, TEMPLATE.TYPE.CATEGORY, categoryTemplateKey));
    };

    // -----------------------------------------------------------------------------------------------------------------
    // RENDER ----------------------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------

    // const notAllTemplatesLoaded = (keys, templates) => {
    //     let notAllLoaded = false;
    //
    //     keys.forEach((key) => {
    //         if (!templates[key]) {
    //             notAllLoaded = true;
    //         }
    //     });
    //
    //     return notAllLoaded;
    // };
    //
    // if (props.useGraph) {
    //     if (!allRequiredTemplatesLoadedFromGraphFlag) {
    //         return null;
    //     }
    // } else if (notAllTemplatesLoaded(categoryKeysArray, categoryTemplateObjects)) {
    //     return null;
    // }

    if (!allRequiredTemplatesLoaded()) {
        return null;
    }

    const template = props.useGraph ? props.questionnaireGraph : (props.questionnaireTemplate ?? {id: 1234567}); // In case in create mode;

    return (
        <div key={"CategoryViewerExpandable-container-div-" + template.id} id={"CategoryViewerExpandable-container-div-" + template.id} style={{ width: "100%" }}>

            <div>&nbsp;</div>

            {/*// ===========================================================================================================*/}
            {/*// CATEGORY LIST DIVIDER */}
            {/*// ===========================================================================================================*/}

            <ArgoTemplateListDividerHook
                fileName="CategoryViewerExpandable"
                parentsLabel="Questionnaire"
                childLabel={["category", "categories"]}
                arrayLength={categoryKeysArray.length}
            />

            {/*// ===========================================================================================================*/}
            {/*// CATEGORY EXPAND/COLLAPSE BUTTON  - NOTE: No expand/collapse button if group is Likert i.e. has answers  */}
            {/*// ===========================================================================================================*/}

            {(categoryKeysArray.length > 1) ?
                <div style={{ float: "right", width: 140 }}>
                    <ArgoExpandCollapseButtonHook
                        fileName="CategoryViewerExpandable"
                        expandAll={reduxExpander.expandAll}
                        onClick={() => dispatch(expanderCollapseAll(props.parentName, TEMPLATE.TYPE.CATEGORY, categoryKeysArray))}
                    />

                    <div>&nbsp;</div>
                </div>
                : ""
            }

            {/*// ===========================================================================================================*/}
            {/*// ASSIGNED CATEGORIES LIST */}
            {/*// ===========================================================================================================*/}

            <div id="CategoryViewerExpandable-question-list-container-div" style={{ display: "flex", alignSelf: "flex-start", flexDirection: "column", width: "100%", minHeight: "min-content" }}>
                {categoryKeysArray.map((categoryTemplateKey, index) => {
                    return (
                        <div key={categoryTemplateKey + index} id={"CategoryViewerExpandable-question-container-div-" + categoryTemplateKey + index}
                             style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                            <Accordion key={"Accordion-" + categoryTemplateKey}
                                id={"CategoryViewerExpandable-Accordion-" + categoryTemplateKey}
                                variant="outlined"
                                disableGutters={true}
                                onChange={(event, open) => {
                                    if (categoryTemplateObjects[categoryTemplateKey]?.groupKeys.length > 0) onExpandChange(categoryTemplateKey, event, open)
                                }}
                                expanded={categoryTemplateObjects[categoryTemplateKey]?.groupKeys.length > 0 ?
                                    (reduxExpander.expander[categoryTemplateKey]?.open ? reduxExpander.expander[categoryTemplateKey]?.open : false) :
                                    false}
                                sx={{ width: "100%", minWidth: 890}}>
                                <AccordionSummary
                                    key={"AccordionSummary-" + categoryTemplateKey}
                                    id={"CategoryViewerExpandable-AccordionSummary-" + categoryTemplateKey}
                                    expandIcon={categoryTemplateObjects[categoryTemplateKey]?.groupKeys.length > 0 ?
                                        <ExpandMoreIcon style={{ color: expander.ICON_COLOR }} /> :
                                        <HorizontalRuleIcon style={{ color: expander.ICON_COLOR }}/>}
                                    aria-controls="panel1-content"
                                    sx={{
                                        height: "30px",
                                        backgroundColor: expander.ACCORDION_BG_COLOR,
                                        "&:hover": { backgroundColor: expander.ACCORDION_BG_COLOR_HOVER },
                                        "& .MuiAccordionSummary-content": {
                                            alignItems: "center",
                                            display: "flex",
                                            gap: 1, // small spacing between groups
                                            minWidth: 0,
                                        }
                                    }}
                                >
                                    {props.showIconButtonReorder && categoryKeysArray.length > 1?
                                        <Box style={{display: "flex", alignItems: "center", minWidth: 80}}>
                                            <ArgoReorderArrowsHook
                                                fileName="CategoryViewerExpandable"
                                                fieldName="container-div"
                                                onClick={onClickIconButtonReorder}
                                                index={index}
                                                arrayLength={props.categoryKeys.length}
                                                isFirst={index === 0}
                                                isLast={index === (props.categoryKeys.length - 1)}
                                                upArrowColor={index === 0 ? expander.ICON_COLOR_HIDDEN : expander.ICON_COLOR}
                                                downArrowColor={props.isLast ? expander.ICON_COLOR_HIDDEN : expander.ICON_COLOR}
                                            />
                                        </Box>: ""
                                    }

                                    <Box style={{display: "flex", alignItems: "center", minWidth: 34}}>
                                        <Typography>
                                            {statusFormatter(categoryTemplateObjects[categoryTemplateKey], 0, 0)}
                                        </Typography>
                                    </Box>

                                    <Box style={{display: "flex", alignItems: "center", minWidth: 92, flex: 1, overflow: "hidden"}}>
                                        <Typography variant="h6" component="div" style={{flexShrink: 0, paddingLeft: 5, minWidth: 92, color: expander.TEXT_COLOR_SECONDARY}}>Category:</Typography>
                                        <Typography variant="h6" component="div"
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        maxWidth: 950,
                                                        paddingLeft: 10,
                                                        color: expander.TEXT_COLOR_PRIMARY,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }}>
                                            {categoryTemplateObjects[categoryTemplateKey].name}
                                        </Typography>
                                    </Box>

                                    {categoryTemplateObjects[categoryTemplateKey].nameQualifier ?
                                        <Box style={{display: "flex", alignItems: "center", minWidth: 258}}>
                                            <Typography variant="h6" component="div" style={{flexShrink: 0, paddingLeft: 5, minWidth: 83, color: expander.TEXT_COLOR_SECONDARY}}>Qualifier:</Typography>
                                            <Typography variant="subtitle1" component="div"
                                                        style={{
                                                            flex: 1,
                                                            minWidth: 175,
                                                            maxWidth: 175,
                                                            paddingLeft: 10,
                                                            color: expander.TEXT_COLOR_PRIMARY,
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis"
                                                        }}>
                                                {categoryTemplateObjects[categoryTemplateKey].nameQualifier ? categoryTemplateObjects[categoryTemplateKey].nameQualifier : ""}
                                            </Typography>
                                        </Box> : ''
                                    }

                                    <Box style={{display: "flex", alignItems: "center", minWidth: 0}}>
                                        <Typography variant="subtitle1" component="div"
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        paddingLeft: 10,
                                                        color: expander.TEXT_COLOR_SECONDARY,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis"
                                                    }}>
                                            {"(Groups Assigned: " + (categoryTemplateObjects[categoryTemplateKey]?.groupKeys ? categoryTemplateObjects[categoryTemplateKey]?.groupKeys.length : "0") + ")"}
                                        </Typography>
                                    </Box>

                                    {props.showIconButtonEdit ?
                                        <Box style={{display: "flex", alignItems: "center", minWidth: 40}}>
                                            <ArgoIconButtonHook
                                                fileName="CategoryViewerExpandable"
                                                fieldName="edit"
                                                onClick={(event) => onClickIconButtonEdit(categoryTemplateObjects[categoryTemplateKey], event)}
                                                index={props.index}
                                                icon={<ModeEditIcon style={{color: expander.ICON_COLOR}}/>}
                                                display={props.showIconButtonEdit}
                                            />
                                        </Box> : ""
                                    }

                                    {props.showIconButtonRemove ?
                                        <Box style={{display: "flex", alignItems: "center", minWidth: 40}}>
                                            <ArgoIconButtonHook
                                                fileName="CategoryViewerExpandable"
                                                fieldName="remove"
                                                onClick={(event) => onClickIconButtonRemove(index, event)}
                                                index={props.index}
                                                icon={<HighlightOffOutlinedIcon
                                                    style={{color: expander.ICON_COLOR}}/>}
                                                display={props.showIconButtonRemove}
                                            />
                                        </Box> : ""
                                    }

                                    {!categoryTemplateObjects[categoryTemplateKey].currentFlag ?
                                        <Box style={{display: "flex", alignItems: "center", minWidth: 40}}>
                                            <ArgoIconButtonHook
                                                fileName="CategoryViewerExpandable"
                                                fieldName="CurrentFlag"
                                                index={props.index}
                                                tooltip="Not the current version"
                                                icon={<FlagCircleIcon style={{color: "#E84343"}}/>}
                                            />
                                        </Box> :
                                        Object.entries(categoryTemplateObjects).some(([key, obj]) => !obj.currentFlag) ?
                                            <Box style={{display: "flex", alignItems: "center", minWidth: 40}}></Box> : ""
                                    }

                                    {props.showIconButtonInfo ?
                                        <Box style={{display: "flex", alignItems: "center", minWidth: 40}}>
                                            <ArgoIconButtonHook
                                                fileName="CategoryViewerExpandable"
                                                fieldName="info"
                                                onClick={(event) => onClickIconButtonInfo(categoryTemplateObjects[categoryTemplateKey], event)}
                                                index={props.index}
                                                tooltip="Show Details"
                                                icon={<InfoOutlinedIcon style={{color: expander.ICON_COLOR}}/>}
                                            />
                                        </Box> : ""
                                    }
                                </AccordionSummary>
                                <AccordionDetails key={"AccordionDetails-" + categoryTemplateKey}>

                                    {/*// ===========================================================================================================*/}
                                    {/*// ASSIGNED GROUPS LIST */}
                                    {/*// ===========================================================================================================*/}

                                    <GroupViewerExpandableHook
                                        parentName={props.parentName}
                                        parentKey={categoryTemplateKey}
                                        groupKeys={categoryTemplateObjects[categoryTemplateKey]?.groupKeys}
                                        showIconButtonReorder={false}
                                        showIconButtonEdit={props.showIconButtonEdit}
                                        showIconButtonRemove={false}
                                        setStateCallback={props.setStateCallback}
                                        hidePortalPreview={true}
                                        useGraph={props.useGraph}
                                        categoryGraph={categoryTemplateObjects[categoryTemplateKey]}
                                    />

                                    <div>&nbsp;</div>

                                    {/*// ===========================================================================================================*/}
                                    {/*// RELATED SHOW GROUPS LIST */}
                                    {/*// ===========================================================================================================*/}

                                    <GroupViewerExpandableHook
                                        parentName={props.parentName}
                                        parentKey={categoryTemplateKey}
                                        groupKeys={categoryTemplateObjects[categoryTemplateKey]?.showGroupKeys}
                                        showIconButtonReorder={false}
                                        showIconButtonEdit={props.showIconButtonEdit}
                                        showIconButtonRemove={false}
                                        setStateCallback={props.setStateCallback}
                                        hidePortalPreview={true}
                                        useGraph={props.useGraph}
                                        categoryGraph={categoryTemplateObjects[categoryTemplateKey]}
                                        isShowGroups={true}
                                    />

                                </AccordionDetails>
                            </Accordion>

                            {(reduxExpander.expander[categoryTemplateKey]?.open) ? <div id={"spacer-div-" + index}>&nbsp;</div> : ""}

                        </div>
                    );
                })}
            </div>

        </div>
    );
}

CategoryViewerExpandableHook.defaultProps = {
    parentName: "",
    questionnaireTemplate: {},
    categoryKeys: [],
    defaultExpanded: false,
    showIconButtonReorder: true,
    showIconButtonEdit: true,
    showIconButtonRemove: true,
    showIconButtonInfo: true,
    hidePortalPreview: false,
    useGraph: false,
    questionnaireGraph: {}
};


CategoryViewerExpandableHook.propTypes = {
    parentName: PropTypes.string,
    questionnaireTemplate: PropTypes.object,
    categoryKeys: PropTypes.array,
    showIconButtonReorder: PropTypes.bool,
    showIconButtonEdit: PropTypes.bool,
    showIconButtonRemove: PropTypes.bool,
    showIconButtonInfo: PropTypes.bool,
    setStateCallback: PropTypes.func,
    hidePortalPreview: PropTypes.bool,
    useGraph: PropTypes.bool,
    questionnaireGraph: PropTypes.object
};

// function mapStateToProps(state, props) {
//
//     let tempExpander = {expanderSet: false, expandAll: true, expander:{}};
//     let expanderExists = false;
//
//     if (state.expander.hasOwnProperty(props.parentName)) {
//         if (state.expander[props.parentName].hasOwnProperty(TEMPLATE.TYPE.CATEGORY)) {
//             expanderExists = true;
//         }
//     }
//
//     return {
//         categoryTemplates: state.categoryTemplate,
//         groupTemplates: state.groupTemplate,
//         questionTemplates: state.questionTemplate,
//         questionsFilter: state.filter[TEMPLATE.FILTER.current.group],
//         expander: expanderExists ? state.expander[props.parentName][TEMPLATE.TYPE.CATEGORY] : tempExpander
//     };
// }
//
// function mapDispatchToProps(dispatch) {
//     return bindActionCreators({getCategoryTemplate, reorderQuestionnaireCategory, removeQuestionnaireCategory, getGroupTemplate, getQuestionTemplate, expanderUpdate, openDrawer}, dispatch);
// }

//export default connect(mapStateToProps, mapDispatchToProps)(CategoryViewerExpandableHook);
export default CategoryViewerExpandableHook;